import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Col, Form, InputGroup } from "react-bootstrap";
import { ExFormLabel } from "../ExControls";
import { useAuth } from "../../contexts/AuthProvider/useAuth";
import { toast } from "react-toastify";
import Select from 'react-select';
import "./styles.css";
import { ButtonCopy } from "../../commons/utils";

const customStyles = {
    container: (baseStyles: any) => ({
        ...baseStyles,
        width: '100%'
    }),
    control: (baseStyles: any, state: any) => ({
        ...baseStyles,
        minHeight: '0px',
        width: '100%',
        border: 'none',
        boxShadow: 'none',
        backgroundColor: state.isDisabled ? 'var(--bg-input-disabled)' : 'var(--background-color)'
    }),
    dropdownIndicator: (baseStyles: any) => ({
        ...baseStyles,
        paddingTop: '2px',
        paddingBottom: '2px',
    }),
    clearIndicator: (baseStyles: any) => ({
        ...baseStyles,
        paddingTop: '2px',
        paddingBottom: '2px',
    }),
    valueContainer: (baseStyles: any) => ({
        ...baseStyles,
        paddingTop: '4px',
        paddingBottom: '4px',

    }),
    input: (baseStyles: any) => ({
        ...baseStyles,
        paddingTop: 0,
        paddingBottom: 0,
        margin: 0,
        color: 'var(--font-color)'
    }),
    indicatorSeparator: (baseStyles: any) => ({
        ...baseStyles,
        marginTop: '4px',
        marginBottom: '4px'
    }),
    menu: (baseStyles: any) => ({
        ...baseStyles,
        left: 0,
        zIndex: 1000,
        fontFamily: "Montserrat",
    }),
    menuList: () => ({
        maxHeight: '300px',
        overflowY: 'auto' as const,
        position: 'relative' as const,
        WebkitOverflowScrolling: 'touch' as const,
        paddingBottom: '4px',
        paddingTop: '4px',
        boxSizing: 'border-box' as const,
        backgroundColor: 'var(--background-color)',
    }),
    option: (baseStyles: any, state: any) => {
        const ativo = state.data?.ativo;
        let borderColor = 'transparent';

        if (ativo === 0) borderColor = '#e6d930';

        return {
            ...baseStyles,
            backgroundColor: state.isSelected ? 'var(--primary-color)' : state.isFocused ? 'rgba(165, 151, 144, 0.1)' : 'transparent',
            borderLeft: `4px solid ${borderColor}`,
            margin: '2px 0px',
            '&:hover': {
                backgroundColor: state.isSelected ? 'var(--primary-color)' : 'rgba(165, 151, 144, 0.1)'
            }
        }
    },
    singleValue: (baseStyles: any, state: any) => ({
        ...baseStyles,
        fontSize: '0.875rem',
        fontFamily: "Montserrat",
        color: 'var(--font-color)'
    }),
    multiValue: (baseStyles: any) => ({
        ...baseStyles,
        margin: 0,
        marginRight: '4px',
        fontFamily: "Montserrat",
    }),
    multiValueLabel: (baseStyles: any) => ({
        ...baseStyles,
        padding: 0,
        fontWeight: 600
    }),
    multiValueRemove: (baseStyles: any) => ({
        ...baseStyles,
        color: '#4d5052',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, .1)',
            color: '#212529',
        },
    })
};

export interface ExSelectProps {
    ref: any;
    id: any;
    defaultValue?: any;
    options?: any;
    label?: String;
    md?: any;
    required?: any;
    model?: any;
    size?: any;
    validated?: any;
    isMulti?: any;
    onAfterChange?: any;
    disabled?: any;
    onFilter?: any;
    copy?: any;
    LeftComponent?: React.ReactNode
}

export interface ExSelectHandler {
    getValue: any
    setValue: any
    getId: any
    validate: any
    setCustomOptions: any
    getDescription: any
    setCustomValue: any
    setFilter: any
    clearFilter: any
}

export const ExSelect: React.FC<ExSelectProps> = forwardRef((props: ExSelectProps, ref: Ref<ExSelectHandler>) => {
    const auth = useAuth();

    const defaultValueApplied = useRef(false);
    const selectRef = useRef<any>(null);

    const [data, setData] = useState<any[]>();
    const [options, setOptions] = useState<any[]>();
    const [value, setValue] = useState<any>();
    const [loading, setLoading] = useState<any>(props.options ? false : true);

    function getValue() {
        if (props.isMulti)
            return value
        else {
            if (!value)
                return null
            else
                return value?.value
        }
    }

    function getDescription() {
        return value?.label
    }

    function getId() {
        return props.id
    }

    function validate() {
        const result = props.required ? ((value !== null) && (value !== undefined) && (value !== '')) : true;
        return result
    }

    function setCustomOptions(_options: any) {
        if (props.model.inactive_field_combo)
            _options.sort((a: any, b: any) => (b.ativo ?? -1) - (a.ativo ?? -1));

        setOptions(_options);

        let exists = false;

        if (Array.isArray(value))
            exists = value.every((val: any) => _options.some((item: any) => item.value === val.value));
        else
            exists = _options.some((item: any) => item.value === value?.value);

        if (!exists)
            setValue(null)
    }

    function setCustomValue(e: any) {
        setValue(e);

        // if (props.onAfterChange)
        //     props.onAfterChange(e);
    }

    async function setFilter(filter: any) {
        loadOptions(data, filter);
    }

    async function clearFilter() {
        loadOptions(data);
    }

    useImperativeHandle(ref, () => ({
        getValue,
        setValue,
        getId,
        validate,
        setCustomOptions,
        getDescription,
        setCustomValue,
        setFilter,
        clearFilter
    }));

    useEffect(() => {
        if (value !== undefined)
            props.onAfterChange?.(value)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    function loadOptions(_data: any, filter?: any) {
        if (filter) {
            _data = _data?.filter((item: any) => {
                return Object.keys(filter).every(key => {
                    const filterValue = filter[key];
                    const itemValue = item[key];

                    if (Array.isArray(filterValue)) {
                        return filterValue.includes(itemValue);
                    }

                    return itemValue === filterValue;
                });
            });
        }

        const _options = _data?.map((item: any) => ({
            value: props.model.key.split('.').length > 1
                ? item[props.model.key.split('.')[0]][props.model.key.split('.')[1]]
                : item[props.model.key],
            label: (props.model.description_field.split('.').length > 1
                ? item[props.model.description_field.split('.')[0]][props.model.description_field.split('.')[1]]
                : item[props.model.description_field]) +
                (props.model.sub_description_field ? ' - ' + item[props.model.sub_description_field] : ''),
            ativo: props.model.inactive_field_combo ? item[props.model.inactive_field_combo] : -1
        }));

        setOptions(_options);

        if (value && value.value) {
            const isValueInOptions = _options.some((option: any) => option.value === value.value);

            if (!isValueInOptions)
                setValue(null);
        }
    }

    async function apiGet(_filter?: any) {
        await auth.get(props.model.api_get + (_filter ? ('?' + _filter) : ''))
            .then((response: any) => {
                if ((props.model.inactive_field) && (!props.model.hide_inactive))
                    response.data = response.data.filter((item: any) => item[props.model.inactive_field] === 1);

                if (props.onFilter)
                    response.data = props.onFilter(response.data);

                if (props.model.inactive_field_combo)
                    response.data.sort((a: any, b: any) => b[props.model.inactive_field_combo] - a[props.model.inactive_field_combo]);

                setData(response.data);
                loadOptions(response.data);
                setLoading(false);
            }).catch((error: any) => {
                console.log(error);
                toast.error('Não foi possível carregar os valores do campo ' + props.label)
            })
    }

    useEffect(() => {
        if (!defaultValueApplied.current && (props.defaultValue !== '') && (props.defaultValue !== undefined) && (options || props.options)) {
            if (props.isMulti) {
                setValue(props.defaultValue);
            } else {
                const _defaultOption = (options || props.options)?.find((option: any) => option.value === props.defaultValue);

                if (_defaultOption) setValue(_defaultOption);
            }

            defaultValueApplied.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, props.options]);

    useMemo(() => {
        if ((!props.options) && (props.model) && (props.model.api_get)) {
            apiGet();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.model]);

    const handleOnChange = (e: any) => {
        setValue(e);

        // if (props.onAfterChange)
        //     props.onAfterChange(e);
    };

    return (
        <Form.Group as={Col} md={props?.md ? props.md : "12"}>
            {props.label && <ExFormLabel>{(props?.required ? '*' : '') + props.label}</ExFormLabel>}
            <InputGroup size={props.size ? props.size : "sm"} hasValidation>
                {props.LeftComponent}
                <Form.Control
                    className="custom_select"
                    ref={selectRef}
                    isInvalid={props.validated && props.required && !value}
                    isValid={props.validated && value}
                    as={Select}
                    onChange={handleOnChange}
                    placeholder=""
                    isLoading={loading}
                    options={props.options ? props.options : options}
                    required={props.required}
                    value={value}
                    styles={customStyles}
                    isMulti={props.isMulti}
                    isDisabled={props.disabled}
                    isClearable={true}
                    noOptionsMessage={() => 'Nenhuma opção disponível'}
                />
                {props.copy && (
                    <InputGroup.Text style={{ padding: 0, background: 'transparent', border: 'none' }}>
                        <ButtonCopy text={getDescription()} />
                    </InputGroup.Text>
                )}
            </InputGroup>
        </Form.Group>
    );
});
